import { extractApiErrorMessage, extractCorrelationId, SILENT_NOT_FOUND } from '@/app/core/errors/api-error'
import { ToastService } from '@/app/shared/services/toast.service'
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular'
import { RedirectRequest } from '@azure/msal-browser'
import { trackEvent } from '@aptabase/web'
import { Observable, catchError, throwError } from 'rxjs'

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  /** Garde contre plusieurs 401 simultanés : un seul loginRedirect à la fois. */
  private redirectInProgress = false

  constructor(
    private toast: ToastService,
    private msal: MsalService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // ProScan (service de scan local, cf. ScannerService) : auth locale (pas B2C) et erreurs déjà
    // gérées par ScanFlowService — on ne toaste pas et surtout on ne déclenche pas de loginRedirect.
    if (req.url.includes('localhost:5215')) {
      return next.handle(req)
    }

    return next.handle(req).pipe(
      catchError((err: unknown) => {
        this.handleError(err, req)
        return throwError(() => err)
      })
    )
  }

  private handleError(err: unknown, req: HttpRequest<unknown>): void {
    const status = err instanceof HttpErrorResponse ? err.status : 0
    const correlationId = extractCorrelationId(err)

    switch (status) {
      case 401:
        // Session expirée / token rejeté : pas de toast, on relance la connexion B2C
        // (même pattern que App.loginRedirect, basé sur la config du MsalGuard).
        this.triggerLoginRedirect()
        return
      case 400:
      case 403:
      case 404:
      case 409:
        // Appel marqué SILENT_NOT_FOUND : son 404 est attendu (ressource purgée par la mutation qui
        // précède), l'appelant le traite lui-même. Toaster ici afficherait une erreur à l'utilisateur
        // pour une opération qui a en réalité RÉUSSI.
        if (status === 404 && req.context.get(SILENT_NOT_FOUND)) return

        // Le backend envoie un CoreErrorResponse : le message métier est dans details[].message.
        this.show(extractApiErrorMessage(err), correlationId)
        return
      default:
        // 500, 0 (réseau) et autres statuts inattendus : message générique + télémétrie (Aptabase).
        // On ne trace PAS les erreurs métier attendues (400/403/404/409) ni le 401 (session) — seulement
        // les pannes serveur/réseau, avec le corrélationId pour recouper avec les logs backend. Sans PII.
        trackEvent('server_error', {
          status,
          correlationId: correlationId ?? '',
        })
        this.show('Le serveur est injoignable ou en erreur. Réessayez.', correlationId)
        return
    }
  }

  private show(message: string, correlationId: string | null): void {
    // Le correlationId reste affiché en suffixe discret pour faciliter le support.
    this.toast.show('error', correlationId ? `${message} • (${correlationId})` : message)
  }

  private triggerLoginRedirect(): void {
    if (this.redirectInProgress) return
    this.redirectInProgress = true
    const authRequest = this.msalGuardConfig.authRequest
    const login$ = authRequest
      ? this.msal.loginRedirect({ ...authRequest } as RedirectRequest)
      : this.msal.loginRedirect()
    login$.subscribe({
      // interaction_in_progress possible si une interaction MSAL est déjà lancée : on l'ignore.
      error: () => undefined,
    })
  }
}
